// D-T51b — Hook para interpretar URL params provenientes de /pulido y disparar
// scroll + edit + focus en el item destino.
//
// Convención URL:
//   ?openId=<id>             → expandir esa fila
//   ?openByNumber=<num>      → buscar por number y expandir
//   ?openBySku=<sku>         → buscar por sku y expandir
//   ?editField=<campo>       → además de expandir, abrir form de edit y focus
//   ?editField=item:<idx>:<campo>  → focus en input dentro de items[idx]
//   ?compare=<id1>,<id2>     → expandir el primero, marcar ambos
//
// Uso típico en cada screen:
//   const link = usePulidoLink({ rows, getId: r => r._id, getNumber: r => r.number });
//   <ExpandableTable initialExpandedId={link.openId} initialEditingId={link.editId} ... />
//   useEffect(() => { link.applyFocus(); }, [link.openId, link.editField]);

import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

const HIGHLIGHT_COLOR = "#f39c12"; // D-T18 amber

export const applyPulidoFocus = ({ rowId, editField, delayMs = 300 }) => {
  if (!rowId) return;

  setTimeout(() => {
    const row = document.querySelector(`[data-row-id="${rowId}"]`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (!editField) return;

    setTimeout(() => {
      const form = document.querySelector(`[data-edit-form="${rowId}"]`);
      if (!form) return;

      let input;
      if (editField.startsWith("item:")) {
        const parts = editField.split(":");
        const idx = parts[1];
        const fieldName = parts[2];
        input = form.querySelector(
          `[data-item-idx="${idx}"] [name="${fieldName}"]`
        );
      } else {
        input = form.querySelector(`[name="${editField}"]`);
      }

      if (input) {
        try { input.focus(); } catch {}
        try { if (typeof input.select === "function") input.select(); } catch {}
        const prev = input.style.boxShadow;
        const prevTransition = input.style.transition;
        input.style.transition = "box-shadow 0.3s";
        input.style.boxShadow = `0 0 0 2px ${HIGHLIGHT_COLOR}`;
        setTimeout(() => {
          input.style.boxShadow = prev || "";
          input.style.transition = prevTransition || "";
        }, 2200);
      }
    }, 350);
  }, delayMs);
};

const usePulidoLink = ({ rows, getId = r => r._id, getNumber, getSku }) => {
  const location = useLocation();
  const appliedRef = useRef({ key: null });

  const params = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      openId: sp.get("openId"),
      openByNumber: sp.get("openByNumber"),
      openBySku: sp.get("openBySku"),
      editField: sp.get("editField"),
      compare: sp.get("compare"),
      action: sp.get("action"),
      suggestedSku: sp.get("suggestedSku"),
    };
  }, [location.search]);

  const compareIds = useMemo(() => {
    if (!params.compare) return null;
    const arr = params.compare.split(",").map(s => s.trim()).filter(Boolean);
    return arr.length ? arr : null;
  }, [params.compare]);

  const resolvedOpenId = useMemo(() => {
    if (params.openId) return params.openId;
    if (compareIds && compareIds.length > 0) return compareIds[0];
    if (!rows || !rows.length) return null;
    if (params.openByNumber && getNumber) {
      const r = rows.find(r => String(getNumber(r)) === String(params.openByNumber));
      if (r) return getId(r);
    }
    if (params.openBySku && getSku) {
      const r = rows.find(r => (getSku(r) || "").toLowerCase() === params.openBySku.toLowerCase());
      if (r) return getId(r);
    }
    return null;
  }, [params, rows, getId, getNumber, getSku, compareIds]);

  const editingId = params.editField ? resolvedOpenId : null;

  // D-T51b-fix — Esperar a que la fila destino esté en `rows` antes de
  // disparar applyPulidoFocus. Si lo dispara antes (con rows aún vacío
  // por fetch async), el querySelector('[data-row-id]') retorna null y
  // el scroll se pierde. Ocurría sobre todo en compare, donde
  // resolvedOpenId = compareIds[0] se resuelve sin chequear rows.
  useEffect(() => {
    if (!resolvedOpenId) return;
    if (!rows || rows.length === 0) return;
    // F5.6 D-T59b — comparar con String() para tolerar casos donde _id
    // pueda venir como ObjectId-like en vez de string (defensivo).
    const target = String(resolvedOpenId);
    const exists = rows.some(r => String(getId(r)) === target);
    if (!exists) return;
    const key = `${resolvedOpenId}::${params.editField || ""}`;
    if (appliedRef.current.key === key) return;
    appliedRef.current.key = key;
    applyPulidoFocus({
      rowId: resolvedOpenId,
      editField: params.editField,
      delayMs: 350,
    });
  }, [resolvedOpenId, params.editField, rows, getId]);

  return {
    openId: resolvedOpenId,
    editingId,
    editField: params.editField,
    compareIds,
    action: params.action,
    suggestedSku: params.suggestedSku,
  };
};

export default usePulidoLink;
